// src/utils/ttmArucoSkew.js
// Compute "how oblique is this shot" from an ArUco marker's four corners.
//
// The marker is physically a SQUARE. Viewed head-on it projects to a square;
// viewed at an angle it projects to a skewed quadrilateral. Measuring how far
// the detected quad departs from a square gives an estimate of viewing
// obliqueness — the thing the AI Stick (two points) cannot see, and the signal
// that decides, per photo:
//   - low skew  -> AI-Stick scale is fine (little foreshortening to correct)
//   - high skew -> engage ArUco + homography (correct the perspective)
// and also drives capture guidance ("square up to the surface").
//
// We compute SEVERAL independent measures. Each catches a different distortion;
// logging all of them lets you learn (from ground-truth) which best predicts
// real measurement error, then pick a threshold.
//
// Input: corners = { tl, tr, br, bl }, each { x, y } in image pixels
//        (exactly what detectArucoCalibration returns).

const dist = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);

// Angle (degrees) at vertex b formed by a-b-c.
const angleAt = (a, b, c) => {
  const v1x = a.x - b.x, v1y = a.y - b.y;
  const v2x = c.x - b.x, v2y = c.y - b.y;
  const dot = v1x * v2x + v1y * v2y;
  const m1 = Math.hypot(v1x, v1y), m2 = Math.hypot(v2x, v2y);
  if (m1 === 0 || m2 === 0) return 0;
  let cos = dot / (m1 * m2);
  cos = Math.max(-1, Math.min(1, cos));
  return (Math.acos(cos) * 180) / Math.PI;
};

export function computeArucoSkew(corners) {
  if (!corners) return null;
  const { tl, tr, br, bl } = corners;
  if (!tl || !tr || !br || !bl) return null;

  // Side lengths
  const top = dist(tl, tr);
  const right = dist(tr, br);
  const bottom = dist(br, bl);
  const left = dist(bl, tl);
  const sides = [top, right, bottom, left];
  const meanSide = (top + right + bottom + left) / 4;
  if (meanSide <= 0) return null;

  // 1) SIDE-RATIO SKEW: opposite sides equal in a square; perspective makes the
  //    near side longer than the far side. Ratio of the larger to smaller of
  //    each opposite pair; 1.0 = head-on, grows with tilt.
  const horizRatio = Math.max(top, bottom) / Math.max(1e-6, Math.min(top, bottom));
  const vertRatio = Math.max(left, right) / Math.max(1e-6, Math.min(left, right));
  const sideRatioSkew = Math.max(horizRatio, vertRatio); // >=1, 1=square-on

  // 2) ANGLE DEVIATION: every corner of a square is 90°. Perspective bends them.
  //    Max absolute deviation from 90°, in degrees.
  const angTL = angleAt(bl, tl, tr);
  const angTR = angleAt(tl, tr, br);
  const angBR = angleAt(tr, br, bl);
  const angBL = angleAt(br, bl, tl);
  const angles = [angTL, angTR, angBR, angBL];
  const maxAngleDevDeg = Math.max(...angles.map((a) => Math.abs(a - 90)));

  // 3) ASPECT DISTORTION: mean horizontal side vs mean vertical side. A square
  //    head-on -> ~1. Strong tilt about one axis squashes one dimension.
  const meanH = (top + bottom) / 2;
  const meanV = (left + right) / 2;
  const aspect = meanH / Math.max(1e-6, meanV); // 1 = balanced
  const aspectSkew = Math.max(aspect, 1 / aspect); // >=1

  // 4) DIAGONAL RATIO: a square's two diagonals are equal. Perspective makes
  //    them unequal. Ratio of longer to shorter; 1 = head-on.
  const d1 = dist(tl, br);
  const d2 = dist(tr, bl);
  const diagRatio = Math.max(d1, d2) / Math.max(1e-6, Math.min(d1, d2));

  // Composite "obliqueness" flag inputs — kept raw so you can threshold later.
  // A rough single score: how far the worst measure is from "head-on".
  const compositeSkew = Math.max(
    sideRatioSkew - 1,
    aspectSkew - 1,
    diagRatio - 1,
    maxAngleDevDeg / 90
  );

  const r3 = (n) => Math.round(n * 1000) / 1000;
  return {
    sideRatioSkew: r3(sideRatioSkew),   // >=1 ; ~1.0 head-on
    maxAngleDevDeg: r3(maxAngleDevDeg),  // degrees off 90 at worst corner
    aspectSkew: r3(aspectSkew),          // >=1 ; H vs V balance
    diagRatio: r3(diagRatio),            // >=1 ; diagonal equality
    compositeSkew: r3(compositeSkew),    // single rough score, 0 = head-on
    meanSidePx: r3(meanSide),            // marker size in px (for size guidance)
  };
}

// Convenience: turn the metrics into a coarse guidance verdict. Thresholds are
// PLACEHOLDERS — tune them from ground-truth (where does stick error exceed
// tolerance? that's where SQUARE_UP should trigger).
export function arucoCaptureVerdict(skew, opts = {}) {
  if (!skew) return { ok: false, reason: "no-marker" };
  const minMarkerPx = opts.minMarkerPx ?? 60;       // below -> too far
  const squareUpComposite = opts.squareUpComposite ?? 0.15; // above -> too oblique

  if (skew.meanSidePx < minMarkerPx) return { ok: false, reason: "too-small", hint: "Move closer — the marker is small in frame." };
  if (skew.compositeSkew > squareUpComposite) return { ok: false, reason: "too-oblique", hint: "Square up — shoot more straight-on to the surface." };
  return { ok: true, reason: "ok" };
}
